import type { PromoClickTarget } from '@supply-mover-2-0/web-ui';

/**
 * Storefront navigation for a promo tile click.
 *
 * The shared `PromoTile` resolves a promo's CTA fields to a
 * `PromoClickTarget` but performs no navigation, because it also renders
 * inside the CRM storefront designer where these routes do not exist.
 * This hook supplies the storefront's half of that contract.
 *
 * Route mapping:
 *   - promo-detail  → /promos/:id
 *   - category      → /catalog?promo=<id>&category=<value>
 *   - item          → /product/<value>
 *   - item-group    → /catalog?promo=<id>
 *   - external-url  → new tab
 *
 * The category destination carries `promo` alongside the category facet
 * so the two intersect. Without it a category CTA lands the customer in
 * the whole category rather than the promoted subset of it — the promo
 * that brought them there stops constraining the results the moment
 * they arrive.
 *
 * The item-group destination needs no second param. `?promo=` already
 * resolves to the promotion's full membership, which includes resolved
 * item-group members, so the group is covered. This previously emitted
 * `?itemGroup=<value>`, a parameter nothing on the catalog ever read —
 * those click-throughs silently landed on an unfiltered catalog.
 */
export function buildPromoHref(target: PromoClickTarget): string | null {
	switch (target.kind) {
		case 'promo-detail':
			return `/promos/${target.promoId}`;
		case 'category':
			return `/catalog?promo=${target.promoId}&category=${encodeURIComponent(target.category)}`;
		case 'item':
			return `/product/${encodeURIComponent(target.itemId)}`;
		case 'item-group':
			return `/catalog?promo=${target.promoId}`;
		case 'external-url':
			// Handled by the caller via window.open — external urls are not
			// router destinations.
			return null;
		default:
			return null;
	}
}
